import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, BaseAreaChart, BaseBarChart, BaseDonutChart, Button, KpiCard, Spinner, Tabs, WidgetShell,
} from '@kss/ui';
import { apiClient } from '@kss/api';
import {
  AlertTriangle, Boxes, ChevronLeft, ChevronRight, Contact, Factory, IndianRupee,
  LayoutDashboard, Package, RefreshCw, ShoppingCart, TrendingUp, Truck, Users, Wallet,
} from 'lucide-react';

/**
 * Main dashboard — a single tabbed/arrow-nav overview that aggregates every
 * module's numbers (Sales, Purchase, Inventory, Parties, HR) on one page so an
 * owner sees the whole business at a glance and can step through each module
 * with the ◄ ► arrows or the tab bar.
 *
 * 100% live data: it fetches the real list endpoints once and reduces them
 * client-side (there are no server-side aggregation endpoints for most of
 * these yet — see BACKEND_GAPS G82/§68). Empty tenants render zeros, never
 * synthetic numbers.
 */

type Row = Record<string, unknown>;
const num = (v: unknown) => (Number(v) || 0);
const str = (v: unknown) => String(v ?? '');

function pickList<T>(r: { data: unknown }): T[] {
  const d = r.data;
  if (Array.isArray(d)) return d as T[];
  if (d && typeof d === 'object') {
    const o = d as { items?: unknown[]; data?: unknown[] | { items?: unknown[] } };
    if (Array.isArray(o.items)) return o.items as T[];
    if (Array.isArray(o.data)) return o.data as T[];
    if (o.data && Array.isArray((o.data as { items?: unknown[] }).items)) return (o.data as { items: unknown[] }).items as T[];
  }
  return [];
}

/** ₹ with Indian grouping; compact (L/Cr) for big KPI numbers. */
function inr(n: number): string {
  return (Number.isFinite(n) ? n : 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
}
function inrShort(n: number): string {
  const a = Math.abs(n);
  if (a >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
  if (a >= 1e5) return `₹${(n / 1e5).toFixed(2)} L`;
  if (a >= 1e3) return `₹${(n / 1e3).toFixed(1)}k`;
  return `₹${inr(n)}`;
}

/** Last 6 calendar months as {key:'YYYY-MM', label:'Jan'}. */
function last6Months(): { key: string; label: string }[] {
  const now = new Date();
  const out: { key: string; label: string }[] = [];
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push({ key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`, label: d.toLocaleDateString('en-IN', { month: 'short' }) });
  }
  return out;
}
const monthKeyOf = (iso: unknown): string => {
  const s = str(iso);
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? '' : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const isThisMonth = (iso: unknown): boolean => monthKeyOf(iso) === monthKeyOf(new Date().toISOString());

/** Sum a numeric field over rows grouped into the last-6-month buckets. */
function monthlySeries(rows: Row[], dateField: string, valueField: string): { month: string; value: number }[] {
  const months = last6Months();
  const buckets = new Map(months.map((m) => [m.key, 0]));
  for (const r of rows) {
    const k = monthKeyOf(r[dateField]);
    if (buckets.has(k)) buckets.set(k, (buckets.get(k) ?? 0) + num(r[valueField]));
  }
  return months.map((m) => ({ month: m.label, value: Math.round(buckets.get(m.key) ?? 0) }));
}

/** Top-N {name,value} by summing a value field grouped by a label field. */
function topBy(rows: Row[], labelField: string, valueField: string, n = 6): { name: string; value: number }[] {
  const m = new Map<string, number>();
  for (const r of rows) {
    const name = str(r[labelField]).trim() || '—';
    m.set(name, (m.get(name) ?? 0) + num(r[valueField]));
  }
  return [...m.entries()].map(([name, value]) => ({ name, value: Math.round(value) }))
    .sort((a, b) => b.value - a.value).slice(0, n);
}

export default function MainDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [data, setData] = useState<{
    salesInv: Row[]; purchInv: Row[]; purchOrd: Row[]; products: Row[];
    stock: Row[]; categories: Row[]; customers: Row[]; suppliers: Row[]; employees: Row[];
  }>({ salesInv: [], purchInv: [], purchOrd: [], products: [], stock: [], categories: [], customers: [], suppliers: [], employees: [] });

  async function load() {
    setLoading(true);
    setErr(null);
    try {
      const get = (p: string) => apiClient.get(p).then(pickList<Row>).catch(() => [] as Row[]);
      const [salesInv, purchInv, purchOrd, products, stock, categories, customers, suppliers, employees] = await Promise.all([
        get('/sales/SalesInvoice'), get('/purchase/PurchaseInvoice'), get('/purchase/PurchaseOrder'),
        get('/master-data/Product'), get('/purchase/StockBalance'), get('/master-data/ProductCategory'),
        get('/crm/Customer'), get('/purchase/Supplier'), get('/hrm/Employee'),
      ]);
      setData({ salesInv, purchInv, purchOrd, products, stock, categories, customers, suppliers, employees });
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { void load(); }, []);

  const m = useMemo(() => {
    const { salesInv, purchInv, purchOrd, products, stock, customers, suppliers, employees, categories } = data;
    // Sales
    const salesRevenue = salesInv.reduce((s, r) => s + num(r.grandTotal ?? r.totalAmount), 0);
    const salesCollected = salesInv.reduce((s, r) => s + num(r.paidAmount), 0);
    const salesOutstanding = salesInv.reduce((s, r) => s + num(r.balance), 0);
    const salesMtd = salesInv.filter((r) => isThisMonth(r.invoiceDate)).reduce((s, r) => s + num(r.grandTotal ?? r.totalAmount), 0);
    // Purchase
    const purchSpend = purchInv.reduce((s, r) => s + num(r.grandTotal ?? r.totalAmount), 0);
    const purchOutstanding = purchInv.reduce((s, r) => s + num(r.balance), 0);
    const purchMtd = purchInv.filter((r) => isThisMonth(r.invoiceDate)).reduce((s, r) => s + num(r.grandTotal ?? r.totalAmount), 0);
    // Inventory
    const stockByProduct = new Map<string, number>();
    for (const s of stock) {
      const id = str(s.productId); if (!id) continue;
      stockByProduct.set(id, (stockByProduct.get(id) ?? 0) + num(s.qty ?? s.quantity));
    }
    const catName = new Map<string, string>();
    for (const c of categories) catName.set(str(c.productCategoryId ?? c.id), str(c.name));
    const prodRows = products.map((p) => {
      const id = str(p.productId ?? p.id);
      const qty = stockByProduct.get(id) ?? 0;
      const cost = num(p.costPrice) || num(p.sellingPrice);
      const reorder = num(p.reorderLevel);
      return {
        name: str(p.name ?? p.code) || `#${id}`, qty, value: qty * cost,
        category: catName.get(str(p.productCategoryId)) || '—',
        status: (qty <= 0 ? 'Out' : reorder > 0 && qty <= reorder ? 'Low' : 'OK') as 'Out' | 'Low' | 'OK',
      };
    });
    const stockValue = prodRows.reduce((s, p) => s + p.value, 0);
    const lowCount = prodRows.filter((p) => p.status === 'Low').length;
    const outCount = prodRows.filter((p) => p.status === 'Out').length;

    return {
      salesRevenue, salesCollected, salesOutstanding, salesMtd, salesCount: salesInv.length,
      purchSpend, purchOutstanding, purchMtd, poCount: purchOrd.length, piCount: purchInv.length,
      skuCount: products.length, stockValue, lowCount, outCount, inStock: prodRows.filter((p) => p.status === 'OK').length,
      custCount: customers.length, supCount: suppliers.length, empCount: employees.length,
      prodRows,
      salesMonthly: monthlySeries(salesInv, 'invoiceDate', 'grandTotal'),
      purchMonthly: monthlySeries(purchInv, 'invoiceDate', 'grandTotal'),
      topCustomers: topBy(salesInv, 'customerName', 'grandTotal'),
      topSuppliers: topBy(purchInv, 'supplierName', 'grandTotal'),
      catMix: (() => {
        const cm = new Map<string, number>();
        for (const p of prodRows) cm.set(p.category, (cm.get(p.category) ?? 0) + p.value);
        return [...cm.entries()].map(([name, value]) => ({ name, value: Math.round(value) })).filter((x) => x.value > 0).sort((a, b) => b.value - a.value).slice(0, 8);
      })(),
      deptMix: (() => {
        const dm = new Map<string, number>();
        for (const e of employees) { const d = str(e.department).trim() || '— Unassigned'; dm.set(d, (dm.get(d) ?? 0) + 1); }
        return [...dm.entries()].map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value).slice(0, 8);
      })(),
      headcountTrend: (() => {
        const months = last6Months();
        return months.map((mo) => {
          const cutoff = new Date(Number(mo.key.slice(0, 4)), Number(mo.key.slice(5)) , 0).getTime();
          const value = employees.filter((e) => {
            const join = Date.parse(str(e.joinDate)); if (Number.isNaN(join) || join > cutoff) return false;
            const exit = e.exitDate ? Date.parse(str(e.exitDate)) : NaN;
            return Number.isNaN(exit) || exit > cutoff;
          }).length;
          return { month: mo.label, value };
        });
      })(),
      recentSales: [...salesInv].sort((a, b) => Date.parse(str(b.invoiceDate)) - Date.parse(str(a.invoiceDate))).slice(0, 8),
      recentPurch: [...purchOrd].sort((a, b) => Date.parse(str(b.orderDate)) - Date.parse(str(a.orderDate))).slice(0, 8),
    };
  }, [data]);

  const TABS = [
    { id: 'overview', label: 'Overview', icon: <LayoutDashboard size={14} /> },
    { id: 'sales', label: 'Sales', icon: <TrendingUp size={14} /> },
    { id: 'purchase', label: 'Purchase', icon: <ShoppingCart size={14} /> },
    { id: 'inventory', label: 'Inventory', icon: <Package size={14} /> },
    { id: 'parties', label: 'Parties', icon: <Contact size={14} /> },
    { id: 'hr', label: 'People', icon: <Users size={14} /> },
  ];
  const [active, setActive] = useState('overview');
  const activeIdx = Math.max(0, TABS.findIndex((t) => t.id === active));
  const step = (delta: number) => setActive(TABS[(activeIdx + delta + TABS.length) % TABS.length].id);

  const views: Record<string, React.ReactNode> = {
    overview: (
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          <KpiCard title="Sales (this month)" value={loading ? '…' : inrShort(m.salesMtd)} icon={<TrendingUp size={18} />} />
          <KpiCard title="Receivables" subtitle="Outstanding" value={loading ? '…' : inrShort(m.salesOutstanding)} icon={<Wallet size={18} />} />
          <KpiCard title="Purchases (this month)" value={loading ? '…' : inrShort(m.purchMtd)} icon={<ShoppingCart size={18} />} />
          <KpiCard title="Stock value" value={loading ? '…' : inrShort(m.stockValue)} icon={<Boxes size={18} />} />
          <KpiCard title="Customers" value={loading ? '…' : m.custCount.toLocaleString()} icon={<Contact size={18} />} />
          <KpiCard title="Headcount" value={loading ? '…' : m.empCount.toLocaleString()} icon={<Users size={18} />} />
        </div>
        <div className="grid gap-4 lg:grid-cols-2">
          <WidgetShell title="Revenue trend (6 months)">
            <BaseAreaChart data={m.salesMonthly} areas={[{ dataKey: 'value', name: 'Revenue ₹' }]} xAxisKey="month" height={260} />
          </WidgetShell>
          <WidgetShell title="Sales vs Purchases (6 months)">
            <BaseBarChart
              data={m.salesMonthly.map((s, i) => ({ month: s.month, Sales: s.value, Purchases: m.purchMonthly[i]?.value ?? 0 }))}
              bars={[{ dataKey: 'Sales', name: 'Sales' }, { dataKey: 'Purchases', name: 'Purchases' }]}
              xAxisKey="month" height={260} showLegend
            />
          </WidgetShell>
        </div>
        <WidgetShell title="Top customers by revenue">
          {m.topCustomers.length ? <BaseDonutChart data={m.topCustomers} height={280} /> : <Empty msg="No sales yet" />}
        </WidgetShell>
      </div>
    ),
    sales: (
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <KpiCard title="Invoices" value={loading ? '…' : m.salesCount.toLocaleString()} icon={<TrendingUp size={18} />} />
          <KpiCard title="Revenue" value={loading ? '…' : inrShort(m.salesRevenue)} icon={<IndianRupee size={18} />} />
          <KpiCard title="Collected" value={loading ? '…' : inrShort(m.salesCollected)} icon={<Wallet size={18} />} />
          <KpiCard title="Outstanding" value={loading ? '…' : inrShort(m.salesOutstanding)} icon={<AlertTriangle size={18} />} />
        </div>
        <div className="grid gap-4 lg:grid-cols-2">
          <WidgetShell title="Monthly revenue"><BaseAreaChart data={m.salesMonthly} areas={[{ dataKey: 'value', name: 'Revenue ₹' }]} xAxisKey="month" height={260} /></WidgetShell>
          <WidgetShell title="Top customers">
            {m.topCustomers.length ? <BaseBarChart data={m.topCustomers} bars={[{ dataKey: 'value', name: '₹' }]} xAxisKey="name" height={260} /> : <Empty msg="No sales yet" />}
          </WidgetShell>
        </div>
        <WidgetShell title="Recent invoices" titleRight={<LinkBtn onClick={() => navigate('/panel/sales-invoices')} label="View all" />}>
          <MiniTable
            cols={['Invoice', 'Date', 'Customer', 'Total', 'Status']}
            rows={m.recentSales.map((r) => [
              str(r.invoiceNumber ?? r.code), fmtDate(r.invoiceDate), str(r.customerName) || '—',
              `₹ ${inr(num(r.grandTotal ?? r.totalAmount))}`, str(r.paymentStatus ?? r.invoiceStatus) || '—',
            ])}
          />
        </WidgetShell>
      </div>
    ),
    purchase: (
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <KpiCard title="Purchase orders" value={loading ? '…' : m.poCount.toLocaleString()} icon={<ShoppingCart size={18} />} />
          <KpiCard title="Purchase invoices" value={loading ? '…' : m.piCount.toLocaleString()} icon={<Truck size={18} />} />
          <KpiCard title="Total spend" value={loading ? '…' : inrShort(m.purchSpend)} icon={<IndianRupee size={18} />} />
          <KpiCard title="Payables" value={loading ? '…' : inrShort(m.purchOutstanding)} icon={<Wallet size={18} />} />
        </div>
        <div className="grid gap-4 lg:grid-cols-2">
          <WidgetShell title="Monthly spend"><BaseAreaChart data={m.purchMonthly} areas={[{ dataKey: 'value', name: 'Spend ₹' }]} xAxisKey="month" height={260} /></WidgetShell>
          <WidgetShell title="Top suppliers">
            {m.topSuppliers.length ? <BaseBarChart data={m.topSuppliers} bars={[{ dataKey: 'value', name: '₹' }]} xAxisKey="name" height={260} /> : <Empty msg="No purchases yet" />}
          </WidgetShell>
        </div>
        <WidgetShell title="Recent purchase orders" titleRight={<LinkBtn onClick={() => navigate('/panel/purchase/orders')} label="View all" />}>
          <MiniTable
            cols={['Order', 'Date', 'Supplier', 'Total', 'Status']}
            rows={m.recentPurch.map((r) => [
              str(r.orderNumber ?? r.code), fmtDate(r.orderDate), str(r.supplierName) || '—',
              `₹ ${inr(num(r.grandTotal ?? r.totalAmount))}`, str(r.orderStatus ?? r.status) || '—',
            ])}
          />
        </WidgetShell>
      </div>
    ),
    inventory: (
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <KpiCard title="SKUs" value={loading ? '…' : m.skuCount.toLocaleString()} icon={<Package size={18} />} />
          <KpiCard title="In stock" value={loading ? '…' : m.inStock.toLocaleString()} icon={<Boxes size={18} />} />
          <KpiCard title="Low stock" value={loading ? '…' : m.lowCount.toLocaleString()} icon={<AlertTriangle size={18} />} />
          <KpiCard title="Out of stock" value={loading ? '…' : m.outCount.toLocaleString()} icon={<AlertTriangle size={18} />} />
          <KpiCard title="Stock value" value={loading ? '…' : inrShort(m.stockValue)} icon={<IndianRupee size={18} />} />
        </div>
        <div className="grid gap-4 lg:grid-cols-2">
          <WidgetShell title="Top products by value">
            {m.prodRows.some((p) => p.value > 0)
              ? <BaseBarChart data={[...m.prodRows].sort((a, b) => b.value - a.value).slice(0, 8).map((p) => ({ name: p.name, value: Math.round(p.value) }))} bars={[{ dataKey: 'value', name: '₹' }]} xAxisKey="name" height={260} />
              : <Empty msg="No stock value — add stock balances" />}
          </WidgetShell>
          <WidgetShell title="Value by category">
            {m.catMix.length ? <BaseDonutChart data={m.catMix} height={280} /> : <Empty msg="No categorised stock value" />}
          </WidgetShell>
        </div>
        <WidgetShell title="Low / out of stock" titleRight={<LinkBtn onClick={() => navigate('/panel/inventory')} label="Inventory" />}>
          <MiniTable
            cols={['Product', 'Category', 'On hand', 'Status']}
            rows={m.prodRows.filter((p) => p.status !== 'OK').slice(0, 10).map((p) => [p.name, p.category, String(p.qty), p.status])}
            empty="Everything is above reorder level"
          />
        </WidgetShell>
      </div>
    ),
    parties: (
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <KpiCard title="Customers" value={loading ? '…' : m.custCount.toLocaleString()} icon={<Contact size={18} />} />
          <KpiCard title="Suppliers" value={loading ? '…' : m.supCount.toLocaleString()} icon={<Truck size={18} />} />
          <KpiCard title="Receivables" value={loading ? '…' : inrShort(m.salesOutstanding)} icon={<Wallet size={18} />} />
          <KpiCard title="Payables" value={loading ? '…' : inrShort(m.purchOutstanding)} icon={<Wallet size={18} />} />
        </div>
        <div className="grid gap-4 lg:grid-cols-2">
          <WidgetShell title="Top customers by revenue">
            {m.topCustomers.length ? <BaseBarChart data={m.topCustomers} bars={[{ dataKey: 'value', name: '₹' }]} xAxisKey="name" height={260} /> : <Empty msg="No sales yet" />}
          </WidgetShell>
          <WidgetShell title="Top suppliers by spend">
            {m.topSuppliers.length ? <BaseBarChart data={m.topSuppliers} bars={[{ dataKey: 'value', name: '₹' }]} xAxisKey="name" height={260} /> : <Empty msg="No purchases yet" />}
          </WidgetShell>
        </div>
        <WidgetShell title="Party-wise report" titleRight={<LinkBtn onClick={() => navigate('/panel/reports/party-wise')} label="Open report" />}>
          <p className="p-4 text-sm text-content-muted">Full customer + supplier ledgers, outstanding and paid totals are in the Party-wise report.</p>
        </WidgetShell>
      </div>
    ),
    hr: (
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <KpiCard title="Headcount" value={loading ? '…' : m.empCount.toLocaleString()} icon={<Users size={18} />} />
          <KpiCard title="Departments" value={loading ? '…' : m.deptMix.length.toLocaleString()} icon={<Factory size={18} />} />
          <KpiCard title="Suppliers" value={loading ? '…' : m.supCount.toLocaleString()} icon={<Truck size={18} />} />
        </div>
        <div className="grid gap-4 lg:grid-cols-2">
          <WidgetShell title="Headcount trend (6 months)">
            {m.empCount ? <BaseAreaChart data={m.headcountTrend} areas={[{ dataKey: 'value', name: 'Employees' }]} xAxisKey="month" height={260} /> : <Empty msg="No employees yet" />}
          </WidgetShell>
          <WidgetShell title="Department mix">
            {m.deptMix.length ? <BaseDonutChart data={m.deptMix} height={280} /> : <Empty msg="No departments set" />}
          </WidgetShell>
        </div>
      </div>
    ),
  };

  return (
    <div className="-m-6 space-y-3 p-[3px]">
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-surface-card px-4 py-3">
        <LayoutDashboard className="text-primary" size={20} />
        <div className="min-w-0 flex-1">
          <h1 className="text-base font-semibold text-content-primary">Business dashboard</h1>
          <p className="text-xs text-content-muted">Live overview across every module — step through with the arrows or tabs.</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Reload
        </Button>
      </div>

      {err && <Alert variant="danger">{err}</Alert>}

      {/* Arrow-nav + tab bar */}
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => step(-1)} aria-label="Previous module" title="Previous module">
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="min-w-0 flex-1 overflow-x-auto">
          <Tabs tabs={TABS.map((t) => ({ ...t, content: null }))} activeId={active} onChange={setActive} variant="arrow" barOnly />
        </div>
        <Button variant="outline" size="sm" onClick={() => step(1)} aria-label="Next module" title="Next module">
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {loading && data.salesInv.length === 0 && data.products.length === 0 ? (
        <div className="flex items-center gap-2 p-6 text-sm text-content-muted"><Spinner /> Loading live data…</div>
      ) : (
        views[active]
      )}
    </div>
  );
}

function Empty({ msg }: { msg: string }) {
  return <div className="flex h-[220px] items-center justify-center text-xs text-content-muted">{msg}</div>;
}

function LinkBtn({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button type="button" onClick={onClick} className="text-xs font-medium text-primary hover:underline">
      {label}
    </button>
  );
}

function MiniTable({ cols, rows, empty }: { cols: string[]; rows: (string | number)[][]; empty?: string }) {
  if (rows.length === 0) {
    return <div className="p-4 text-sm text-content-muted">{empty ?? 'No rows.'}</div>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-left text-[11px] uppercase tracking-wider text-content-muted">
            {cols.map((c, i) => <th key={c} className={`px-3 py-2 font-medium ${i >= 3 ? 'text-right' : ''}`}>{c}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, ri) => (
            <tr key={ri} className="border-b border-border/50 last:border-0">
              {r.map((cell, ci) => (
                <td key={ci} className={`px-3 py-2 ${ci >= 3 ? 'text-right tabular-nums' : ''} ${ci === 0 ? 'font-medium text-content-primary' : 'text-content-secondary'}`}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function fmtDate(v: unknown): string {
  const s = str(v);
  if (!s) return '—';
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? s.slice(0, 10) : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' });
}
